import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  useLayoutEffect,
  useCallback,
  ReactNode,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

// Images
import logoSm from "../assets/images/logo-sm.png";
import logoDark from "../assets/images/logo-dark.png";

// Components
import PageTitle from "../Components/Common/PageTitle";
import BreadCrumb from "../Components/Common/BreadCrumb";
import CommonForm, { FormFieldConfig } from "../Components/Common/CommonForm";

// Redux
import { changeSidebarVisibility } from "../slices/thunks";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import type { Dispatch } from "@reduxjs/toolkit";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import { ALIQOUT_PATHS, DISPENSING_LOG_PATHS, LINE_CLEARANCE_PATHS, IP_PATHS, LIBRARY_PATHS, MASTER_AE_PATHS, MASTER_PERIOD_PATHS, MASTER_STUDY_PATHS, RETURNS_LOG_PATHS, SPONSOR_PATHS, STUDY_PATHS, VOLUNTEER_PATHS, STOCK_RECONCILIATION_PATHS, PRINT_LABEL_PATHS, ACCESSIONING_PATHS, PRINT_HISTORY_PATHS, RETENTION_PATHS, DISPOSAL_PATHS, TRANSFER_PATHS, AUDIT_PATHS, MASTER_TG_PATHS, SCANNING_PATHS, REPORTS_PATHS, BLOCK_VOLUNTEER_PATHS, DROPOUT_VOLUNTEER_PATHS, TRANSFER_VOLUNTEER_PATHS, AMBULATORY_VISIT_PATHS, DASHBOARD_PATHS, VERIFICATION_PATHS, STUDY_LITE_PATHS,USER_ROUTES,IP_STORAGE_PATHS,STORAGE_PATHS } from "Routes/paths";
import { useGlobalStudy } from "../context/StudyContext";
import { getFirstAccessiblePath, GetUserData } from "common/service/CommonService";
import { usePermission } from "context/PermissionContext";
import { PERMISSION_MODULES, PERMISSION_ACTIONS } from "common/constants/PermissionConstants";
import { useVerificationService } from "modules/Verification/api/useVerificationQuery";
import VerificationQueue from "modules/Verification/pages/VerificationQueue";
import FullPageLoader from "Components/Common/FullPageLoader";

/* ---------- Types ---------- */

interface BreadcrumbItem {
  text: string;
  link?: string;
  onclick?: () => void;
}

interface BreadcrumbContextType {
  breadcrumbItems: BreadcrumbItem[];
  setBreadcrumbItems: React.Dispatch<React.SetStateAction<BreadcrumbItem[]>>;
}

interface HeaderProps {
  onChangeLayoutMode?: (value: string) => void;
  layoutModeType?: string;
  headerClass?: string;
}

interface LayoutState {
  sidebarVisibilitytype: string;
}

interface RootState {
  Layout: LayoutState;
}

/* ---------- Breadcrumb Context ---------- */

const BreadcrumbContext = createContext<{
  breadcrumbItems: BreadcrumbItem[];
  setBreadcrumbItems: React.Dispatch<React.SetStateAction<BreadcrumbItem[]>>;
} | null>(null);

/* ---------- Header Component ---------- */

const Header: React.FC<HeaderProps> = ({
  onChangeLayoutMode,
  layoutModeType,
  headerClass = "",
}) => {
  const breadcrumbContext = useContext(BreadcrumbContext);
  const breadcrumbItems = breadcrumbContext?.breadcrumbItems ?? [];

  const dispatch: Dispatch<any> = useDispatch();
  const navigate = useNavigate();
  const { isModuleVisible } = usePermission();

  // Initialize form for study selection
  const { control, setValue } = useForm<{ selectedStudy: string | number | null }>({
    defaultValues: {
      selectedStudy: null,
    },
  });

  const selectDashboardData = createSelector(
    (state: RootState) => state.Layout,
    (layout) => layout.sidebarVisibilitytype,
  );

  const sidebarVisibilitytype = useSelector(selectDashboardData);

  const [search, setSearch] = useState<boolean>(false);
  const toggleSearch = (): void => {
    setSearch((prev) => !prev);
  };

  /* ---------- Live clock ---------- */


  const location = useLocation();
  const [pageTitle, setPageTitle] = useState<string>("Dashboard");
  const user = GetUserData();
  const { hasPermission } = usePermission();

  const { DropDownAllQuery: studySelectAllQuery } = useMasterStudyService({
    includedraft: location.pathname === MASTER_STUDY_PATHS.LIST,
    enabledDropDown: true
  });

  const hasAllStudiesRight = hasPermission(PERMISSION_MODULES.GLOBAL_STUDIES, PERMISSION_ACTIONS.ALL_STUDIES);

  const studyOptions: Array<{
    label: string | number;
    value: string | number;
  }> = hasAllStudiesRight
      ? (((studySelectAllQuery.data as any)?.data || []).map((study: any) => ({
        label: study.studycode,
        value: study.studyid,
      })))
      : ((user?.studies as any[])?.map((study: any) => ({
        label: study.studycode,
        value: study.studyid,
      })) || []);

  const hasUserStudies = Array.isArray(user?.studies) && user.studies.length > 0;

  const { selectedStudy: globalStudy, setGlobalStudy } = useGlobalStudy();
  const [isVQDrawerOpen, setIsVQDrawerOpen] = useState<boolean>(false);

  // Notification count from API
  const { NotificationCountQuery } = useVerificationService({
    studyid: globalStudy?.studyid ? Number(globalStudy.studyid) : null,
  });

  const notificationCount: number = NotificationCountQuery?.data?.data;

  useEffect(() => {
    const globalStudyInOptions = globalStudy && studyOptions.some(
      (s: any) => String(s.value) === String(globalStudy.studyid)
    );

    if (user?.studyid) {
      if (!globalStudy || String(globalStudy.studyid) !== String(user.studyid)) {
        setGlobalStudy({ studyid: String(user.studyid), studyname: String(user.studycode) });
      }
    } else if (!globalStudy || !globalStudyInOptions) {
      if (studyOptions.length > 0) {
        const firstStudy = studyOptions[0];
        setGlobalStudy({ studyid: String(firstStudy.value), studyname: String(firstStudy.label) });
      }
    }

    if (globalStudy) {
      const foundOption = studyOptions.find(
        (opt: any) => String(opt.value) === String(globalStudy.studyid)
      );
      setValue("selectedStudy", foundOption ? Number(foundOption.value) : Number(globalStudy.studyid));
    } else {
      setValue("selectedStudy", null);
    }
  }, [globalStudy, studyOptions, setValue, user?.studyid, user?.studycode, setGlobalStudy]);

  // Form field configuration for study selection
  const studyFormFields: FormFieldConfig[] = [
    {
      name: "selectedStudy",
      type: "select",
      placeholder: "Select Study",
      options: studyOptions,
      disabled: !!user?.studyid,
      isClearable: false,
      onChangeExtra(selected) {
        if (selected) {
          setGlobalStudy({ studyid: String(selected.value), studyname: String(selected.label) });
        } else {
          setGlobalStudy(null);
        }
        const firstAccessiblePath = getFirstAccessiblePath(undefined, undefined, { isModuleVisible, hasPermission });
        navigate(firstAccessiblePath);
      },
    },
  ];

  /* ---------- Page title based on route ---------- */
  useEffect(() => {
    const path = location.pathname;

    switch (true) {
      case path === LIBRARY_PATHS.CRF_LIST:
        setPageTitle("CRF");
        break;

      case path === LIBRARY_PATHS.TEMPLATE_LIST:
        setPageTitle("Template");
        break;

      case path === LIBRARY_PATHS.CODE_LIST:
        setPageTitle("Codelist");
        break;

      case path?.startsWith(LIBRARY_PATHS.CHECKLIST_LIST):
        setPageTitle("Checklist");
        break;

      case path === LIBRARY_PATHS.WORKFLOW_LIST:
        setPageTitle("Workflow");
        break;

      case path === LIBRARY_PATHS.ROLE_LIST:
        setPageTitle("Role");
        break;

      case path === USER_ROUTES.LIST:
        setPageTitle("User");
        break;

      case path === LIBRARY_PATHS.DESIGN_LIST:
        setPageTitle("Design");
        break;

      case path === VOLUNTEER_PATHS.LIST:
        setPageTitle("Volunteers");
        break;
      case path === STUDY_PATHS.SCREENING_LIST:
        setPageTitle("Study Screening");
        break;
      case path === STUDY_PATHS.LIST:
        setPageTitle("Study");
        break;

      case path === ALIQOUT_PATHS.LIST:
        setPageTitle("Aliquot");
        break;

      case path === SPONSOR_PATHS.LIST:
        setPageTitle("Sponsor");
        break;

      case path === MASTER_PERIOD_PATHS.LIST:
        setPageTitle("Period");
        break;

      case path === MASTER_STUDY_PATHS.LIST:
        setPageTitle("Study");
        break;

      case path === MASTER_AE_PATHS.LIST:
        setPageTitle("Adverse Event");
        break;

      case path === STUDY_LITE_PATHS.LIST:
        setPageTitle("Study Lite");
        break;

      case path.startsWith(STUDY_LITE_PATHS.ADD):
        setPageTitle("Add Study ");
        break;

      case path.startsWith(STUDY_LITE_PATHS.EDIT.split(":id")[0]):
        setPageTitle("Edit Study Lite");
        break;

      case path.startsWith(STUDY_LITE_PATHS.VIEW.split(":id")[0]):
        setPageTitle("View Study Lite");
        break;

      case path.startsWith(AMBULATORY_VISIT_PATHS.VIEW.split(":id")[0]):
        setPageTitle("View Ambulatory Visit");
        break;

      case /^\/ambulatory-visit\/detail\/[^/]+\/subject\/[^/]+/.test(path):
        setPageTitle("Ambulatory Subject Detail");
        break;

      case /^\/post-study-assessment\/subject\/[^/]+/.test(path):
        setPageTitle("Post Study Assessment Subject Detail");
        break;

      case path.startsWith(AMBULATORY_VISIT_PATHS.DETAIL.split(':visitId')[0]):
        setPageTitle("Ambulatory Visit Details");
        break;

      case path === MASTER_AE_PATHS.ADD:
        setPageTitle("Log New Adverse Event");
        break;

      case path === RETENTION_PATHS.ADD:
        setPageTitle("Add Drug Retention");
        break;

      case path === RETENTION_PATHS.LIST:
        setPageTitle("Drug Retention");
        break;

      case path === AUDIT_PATHS.LOGS:
        setPageTitle("Global Audit Trail");
        break;

      case path === AUDIT_PATHS.USER_ACTIVITY:
        setPageTitle("User Activity");
        break;

      case path.startsWith('/adverse-event/edit/'):
        setPageTitle("Edit Adverse Event");
        break;

      case path.startsWith(RETENTION_PATHS.VIEW.split(':id')[0]):
        setPageTitle("View Drug Retention");
        break;

      case path.startsWith(RETENTION_PATHS.EDIT.split(':id')[0]):
        setPageTitle("Edit Drug Retention");
        break;
      case path === DISPOSAL_PATHS.ADD:
        setPageTitle("New Disposal Request");
        break;
      case path === DISPOSAL_PATHS.LIST:
        setPageTitle("Drug Destruction & Disposal");
        break;
      case path.startsWith(DISPOSAL_PATHS.VIEW.split(':id')[0]):
        setPageTitle("View Disposal Protocol");
        break;
      case path.startsWith(DISPOSAL_PATHS.EDIT.split(':id')[0]):
        setPageTitle("Edit Disposal Protocol");
        break;

      case path === TRANSFER_PATHS.LIST:
        setPageTitle("Transfer");
        break;

      case path === TRANSFER_PATHS.ADD:
        setPageTitle("Add Transfer");
        break;

      case path.startsWith(TRANSFER_PATHS.VIEW.split(':id')[0]):
        setPageTitle("View Transfer");
        break;

      case path.startsWith(TRANSFER_PATHS.EDIT.split(':id')[0]):
        setPageTitle("Edit Transfer");
        break;

      case path.startsWith('/adverse-event/view/'):
        setPageTitle("View Adverse Event");
        break;

      case path === MASTER_TG_PATHS.LIST:
      case path === MASTER_TG_PATHS.LIST:
        setPageTitle("Training Group");
        break;

      case path.startsWith(MASTER_TG_PATHS.EDIT.split(':id')[0]):
        setPageTitle("Edit Training Group");
        break;

      case path.startsWith(MASTER_TG_PATHS.VIEW.split(':id')[0]):
        setPageTitle("View Training Group");
        break;

      case path.startsWith(MASTER_TG_PATHS.ASSIGN_TRAINING.split(':id')[0]):
        const groupName = location.state?.traininggroup;
        setPageTitle(groupName ? `Assign Training to ${groupName}` : "Assign Training");
        break;

      case path === IP_PATHS.LIST:
        setPageTitle("IP Inventory");
        break;

      case path.startsWith(IP_PATHS.EDIT.split(':id')[0]):
        setPageTitle("Edit IP Inventory");
        break;

      case path === IP_PATHS.ADD:
        setPageTitle("Add IP Inventory");
        break;

      case path.startsWith(IP_PATHS.VIEW.split(':id')[0]):
        setPageTitle("View IP Inventory");
        break;

      case path === IP_STORAGE_PATHS.LIST:
        setPageTitle("IP Storage");
        break;

      case path === IP_STORAGE_PATHS.ADD:
        setPageTitle("Add IP Storage");
        break;

      case path === LINE_CLEARANCE_PATHS.LIST:
        setPageTitle("Line Clearance");
        break;

      case path === LINE_CLEARANCE_PATHS.ADD:
        setPageTitle("Add Line Clearance");
        break;

      case path.startsWith(LINE_CLEARANCE_PATHS.EDIT.split(':id')[0]):
        setPageTitle("Edit Line Clearance");
        break;

      case path.startsWith(LINE_CLEARANCE_PATHS.VIEW.split(':id')[0]):
        setPageTitle("View Line Clearance");
        break;

      case path === MASTER_STUDY_PATHS.ATTACHMENTS:
        setPageTitle("Document Management");
        break;

      case path === DISPENSING_LOG_PATHS.LIST:
        setPageTitle("Dispensing Log");
        break;

      case path === DISPENSING_LOG_PATHS.ADD:
        setPageTitle("Add Dispensing Log");
        break;

      case path.startsWith(DISPENSING_LOG_PATHS.VIEW.split(':id')[0]):
        setPageTitle("View Dispensing Log");
        break;

      case path.startsWith(DISPENSING_LOG_PATHS.EDIT.split(':id')[0]):
        setPageTitle("Edit Dispensing Log");
        break;

      case path === RETURNS_LOG_PATHS.LIST:
        setPageTitle("Returns Log");
        break;

      case path === RETURNS_LOG_PATHS.ADD:
        setPageTitle("Add Returns Log");
        break;

      case path.startsWith(RETURNS_LOG_PATHS.VIEW.split(':id')[0]):
        setPageTitle("View Returns Log");
        break;

      case path.startsWith(RETURNS_LOG_PATHS.EDIT.split(':id')[0]):
        setPageTitle("Edit Returns Log");
        break;

      case path === PRINT_LABEL_PATHS.TABS:
        setPageTitle("Print Label");
        break;

      case path === "/print-labels/icard":
        setPageTitle("Print ID Card");
        break;

      case path === "/print-labels/vacutainer":
        setPageTitle("Print Vacutainer");
        break;

      case path === "/print-labels/cryovial":
        setPageTitle("Print Cryovial");
        break;

      case path === "/print-labels/dispensing":
        setPageTitle("Print Dispensing");
        break;

      case path === ACCESSIONING_PATHS.LIST:
        setPageTitle("Accessioning Sessions");
        break;

      case path.startsWith(ACCESSIONING_PATHS.MANUAL_ENTRY):
        setPageTitle("Manual Entry");
        break;

      case path === REPORTS_PATHS.SUMMARY:
        setPageTitle("Summary Reports");
        break;

      case path === REPORTS_PATHS.STOCK_SUMMARY:
        setPageTitle("IP Stock Summary Report");
        break;

      case path === REPORTS_PATHS.STOCK_LEDGER:
        setPageTitle("IP Stock Ledger Report");
        break;

      case path === REPORTS_PATHS.SUBJECT_WISE:
        setPageTitle("IP Subjectwise Distribution Report");
        break;

      case path === REPORTS_PATHS.EXPIRING_SOON:
        setPageTitle("IP Stock Expiring Report");
        break;

      case path === BLOCK_VOLUNTEER_PATHS.LIST:
        setPageTitle("Block Volunteer");
        break;

      case path === BLOCK_VOLUNTEER_PATHS.ADD:
        setPageTitle("Add Block Record");
        break;

      case path.startsWith(BLOCK_VOLUNTEER_PATHS.EDIT.split(':id')[0]):
        setPageTitle("Edit Block Record");
        break;

      case path.startsWith(BLOCK_VOLUNTEER_PATHS.VIEW.split(':id')[0]):
        setPageTitle("View Block Record");
        break;

      case path === DROPOUT_VOLUNTEER_PATHS.ADD:
        setPageTitle("Add Dropout Record");
        break;

      case path === DROPOUT_VOLUNTEER_PATHS.LIST:
        setPageTitle("Dropout Management");
        break;

      case path === TRANSFER_VOLUNTEER_PATHS.ADD:
        setPageTitle("Add Transfer Record");
        break;

      case path === TRANSFER_VOLUNTEER_PATHS.LIST:
        setPageTitle("Transfer Management");
        break;

      case path.startsWith(TRANSFER_VOLUNTEER_PATHS.EDIT.split(':id')[0]):
        setPageTitle("Edit Transfer Record");
        break;

      case path.startsWith(TRANSFER_VOLUNTEER_PATHS.VIEW.split(':id')[0]):
        setPageTitle("View Transfer Record");
        break;

      case path.startsWith(DROPOUT_VOLUNTEER_PATHS.EDIT.split(':id')[0]):
        setPageTitle("Edit Dropout Record");
        break;

      case path.startsWith(DROPOUT_VOLUNTEER_PATHS.VIEW.split(':id')[0]):
        setPageTitle("View Dropout Record");
        break;

      case path.startsWith(VERIFICATION_PATHS.SAMPLE_COLLECTION.split(':periodid')[0]):
        setPageTitle("Sample Collection Verification");
        break;

      case path.startsWith(ACCESSIONING_PATHS.LOGS.split(':id')[0]):
      case path.startsWith(SCANNING_PATHS.LOGS.split(':id')[0]):
        setPageTitle("Session Log");
        break;

      case path.startsWith(ACCESSIONING_PATHS.SESSION.split(':id')[0]):
        setPageTitle("Active Session");
        break;
      case path.startsWith(SCANNING_PATHS.SESSION.split(':id')[0]):
        setPageTitle("Active Session");
        break;

      case path === "/company":
        setPageTitle("Company");
        break;

      case path === "/companyregister":
        setPageTitle("Register Company");
        break;

      case path === "/change-password":
        setPageTitle("Change Password");
        break;

      case path === "/settings" || path === "/setting":
        setPageTitle("System Settings");
        break;

      case path === "/audit":
        setPageTitle("Audit");
        break;

      case path === "/rightsaudit":
        setPageTitle("Rights Audit");
        break;

      case path === "/useractivitylogaudit":
        setPageTitle("User Audit");
        break;

      case path === "/stock-reconciliation/list":
        setPageTitle("Daily Stock Reconciliation");
        break;

      case path === PRINT_HISTORY_PATHS.LIST:
        setPageTitle("Print History & Reprint");
        break;

      case path === SCANNING_PATHS.VIEW:
        setPageTitle("Scanning");
        break;

      case path === STORAGE_PATHS.MAIN:
        setPageTitle("Clinical Storage Management");
        break;

      case path.startsWith(STORAGE_PATHS.UNIT_DETAIL.split(":unitId")[0]):
        setPageTitle("Storage Unit Details");
        break;

      default:
        setPageTitle("Dashboard");
        break;
    }
  }, [location.pathname]);

  /* ---------- Toggle Sidebar ---------- */
  const toggleMenuBtn = (): void => {
    const windowSize = document.documentElement.clientWidth;

    dispatch(changeSidebarVisibility("show"));

    if (windowSize > 767) {
      document.querySelector(".hamburger-icon")?.classList.toggle("open");
    }

    const layout = document.documentElement.getAttribute("data-layout");

    if (layout === "horizontal") {
      document.body.classList.toggle("menu");
    }

    if (
      sidebarVisibilitytype === "show" &&
      (layout === "vertical" || layout === "semibox")
    ) {
      if (windowSize < 1025 && windowSize > 767) {
        document.body.classList.remove("vertical-sidebar-enable");
        document.documentElement.getAttribute("data-sidebar-size") === "sm"
          ? document.documentElement.setAttribute("data-sidebar-size", "")
          : document.documentElement.setAttribute("data-sidebar-size", "sm");
      } else if (windowSize > 1025) {
        document.body.classList.remove("vertical-sidebar-enable");
        document.documentElement.getAttribute("data-sidebar-size") === "lg"
          ? document.documentElement.setAttribute("data-sidebar-size", "sm")
          : document.documentElement.setAttribute("data-sidebar-size", "lg");
      } else if (windowSize <= 767) {
        document.body.classList.add("vertical-sidebar-enable");
        document.documentElement.setAttribute("data-sidebar-size", "lg");
      }
    }

    if (layout === "twocolumn") {
      document.body.classList.toggle("twocolumn-panel");
    }
  };

  if(studySelectAllQuery.isLoading){
    return <FullPageLoader />;
  }

  return (
    <React.Fragment>
      <header id="page-topbar" className={headerClass}>
        <div className="layout-width">
          <div className="navbar-header">

            {/* ── Left: hamburger + page title ── */}
            <div className="d-flex align-items-center gap-2">
              <div className="navbar-brand-box horizontal-logo">
                <Link to="/" className="logo logo-dark">
                  <span className="logo-sm">
                    <img src={logoSm} alt="logo small" height="22" />
                  </span>
                  <span className="logo-lg">
                    <img src={logoDark} alt="logo dark" height="17" />
                  </span>
                </Link>
              </div>

              <button
                onClick={toggleMenuBtn}
                type="button"
                className="btn btn-sm header-item vertical-menu-btn topnav-hamburger"
                id="topnav-hamburger-icon"
              >
                <span className="hamburger-icon">
                  <span />
                  <span />
                  <span />
                </span>
              </button>

              <PageTitle pageTitle={pageTitle} />

              {/* Breadcrumbs – shown inline next to title when present */}
              {breadcrumbItems.length > 0 && (
                <div className="d-none d-md-flex align-items-center ms-2">
                  <span className="header-divider"></span>
                  <BreadCrumb items={breadcrumbItems} />
                </div>
              )}
            </div>

            {/* ── Right: clock + study dropdown ── */}
            <div className="header-right-section">

              {/* Live clock */}


              <span className="header-divider"></span>

              {/* Study selector */}
              <div
                className="study-dropdown-container"
                style={{ minWidth: "220px" }}
              >
                <CommonForm
                  fields={studyFormFields}
                  control={control}
                  setValue={setValue}
                  columns={1}
                  parentClass="m-0"
                  labelShow={false}
                  childClass="mb-0"
                />
              </div>

              {/* Notification Bell */}
              <button
                type="button"
                className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle header-notification-btn position-relative"
                title="Notifications"
                id="notification-bell-btn"
                onClick={() => setIsVQDrawerOpen(true)}
                style={{ top: '4px' }}
              >
                <i className="ri-notification-3-line fs-22"></i>
                <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger">
                  {NotificationCountQuery.isFetching ? (
                    <span
                      style={{
                        display: 'inline-block',
                        width: '6px',
                        height: '6px',
                        borderRadius: '4px',
                        background: 'rgba(255,255,255,0.5)',
                        animation: 'pulse 1s ease-in-out infinite',
                      }}
                    />
                  ) : (
                    notificationCount
                  )}
                </span>
              </button>
            </div>

          </div>
        </div>
      </header>
      <VerificationQueue
        isOpen={isVQDrawerOpen}
        toggle={() => setIsVQDrawerOpen(false)}
      />
    </React.Fragment>
  );
};

/* ---------- Breadcrumb Provider ---------- */

const BreadcrumbProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [breadcrumbItems, setBreadcrumbItems] = useState<BreadcrumbItem[]>([]);
  const location = useLocation();

  // Clear breadcrumbs on every route change by default. Use a layout
  // effect so this clearing runs before page-level effects that set
  // breadcrumbs, preventing a race where the provider would clear
  // after a page set its breadcrumb.

  useLayoutEffect(() => {
    setBreadcrumbItems([]);
  }, [location.pathname]);

  const breadcrumbContextValue = useMemo(
    () => ({ breadcrumbItems, setBreadcrumbItems }),
    [breadcrumbItems],
  );

  return (
    <BreadcrumbContext.Provider value={breadcrumbContextValue}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

export { BreadcrumbProvider, BreadcrumbContext };
export default Header;
 